import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Heart, Clock, Upload, X } from "lucide-react";
import { api } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1550376026-7375b92bb318?w=1600";

const EMPTY = {
  name: "", email: "", phone: "",
  category: "borsa",
  description: "",
  color_preference: "",
  size_preference: "",
  budget: "",
  desired_date: "",
  inspiration_image: "",
};

export default function SuMisura() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) {
      toast.error("Immagine troppo grande", { description: "Max 2MB" });
      return;
    }
    const r = new FileReader();
    r.onload = () => setField("inspiration_image", r.result);
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/custom-orders", form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Richiesta inviata", { description: "Ti contatteremo a breve." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || "Riprova più tardi." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="sumisura-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end">
        <div className="absolute inset-0 hero-image-wrapper">
          <img src={HERO} alt="Filati Alimì" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pb-16 lg:pb-20">
          <div className="max-w-xl text-white fade-in-up">
            <span className="overline text-alimi-rose">Su Misura</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 tracking-tight">
              Pensato<br />solo per <em className="italic text-alimi-rose">te</em>.
            </h1>
            <p className="text-sm sm:text-base text-white/85 mt-7 max-w-md leading-relaxed">
              Una borsa unica, un capo speciale, un regalo che racconta una storia.
              Realizziamo creazioni personalizzate su tua richiesta.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="overline">Come funziona</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 tracking-tight">Tre passi, un'idea<br />che diventa realtà.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Sparkles, title: "Raccontaci", text: "Compila il modulo qui sotto descrivendo la tua idea: colore, dimensione, stile, occasione." },
            { icon: Heart, title: "Disegniamo insieme", text: "Ti rispondiamo entro 48 ore con un preventivo, una proposta visiva e i materiali consigliati." },
            { icon: Clock, title: "Creiamo a mano", text: "Una volta confermato, lavoriamo il tuo pezzo unico. Tempi medi: 2-4 settimane." },
          ].map((s, i) => (
            <div key={i} className="text-center fade-in-up" style={{ animationDelay: `${0.1 * i}s` }}>
              <div className="w-14 h-14 mx-auto rounded-full border border-alimi-gold flex items-center justify-center text-alimi-gold">
                <s.icon size={22} strokeWidth={1.4} />
              </div>
              <div className="font-serif text-2xl mt-6">{s.title}</div>
              <p className="text-sm text-alimi-inkSoft mt-3 leading-relaxed max-w-xs mx-auto">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="bg-alimi-surface2 py-24">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-xl mx-auto mb-12">
            <span className="overline">Richiesta personalizzata</span>
            <h2 className="font-serif text-4xl mt-4 tracking-tight">Raccontaci la tua idea</h2>
            <div className="alimi-divider mt-6" />
          </div>

          {sent ? (
            <div data-testid="sumisura-success" className="bg-alimi-bg p-12 text-center border border-alimi-border">
              <div className="overline">Grazie</div>
              <h3 className="font-serif text-3xl mt-3">Abbiamo ricevuto la tua richiesta.</h3>
              <p className="text-sm text-alimi-inkSoft mt-5 max-w-md mx-auto leading-relaxed">
                Ti risponderemo entro 48 ore via email con una proposta su misura.
                Nel frattempo… preparati a sognare un po'. 🤍
              </p>
              <button onClick={() => setSent(false)} className="btn-outline mt-8" data-testid="new-request-btn">
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-alimi-bg p-8 lg:p-12 border border-alimi-border space-y-6" data-testid="sumisura-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Nome e cognome *</label>
                  <input data-testid="sm-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Email *</label>
                  <input data-testid="sm-email" required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Telefono</label>
                  <input data-testid="sm-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Cosa desideri? *</label>
                  <select data-testid="sm-category" value={form.category} onChange={(e) => setField("category", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm">
                    <option value="borsa">Una borsa</option>
                    <option value="accessorio">Un accessorio</option>
                    <option value="abbigliamento">Un capo d'abbigliamento</option>
                    <option value="altro">Altro / idea regalo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Descrivi la tua idea *</label>
                <textarea data-testid="sm-description" required rows={5} value={form.description} onChange={(e) => setField("description", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm resize-none" placeholder="Es. Vorrei una borsa media a tracolla, stile bohémien, con frange…" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Colori preferiti</label>
                  <input data-testid="sm-color" value={form.color_preference} onChange={(e) => setField("color_preference", e.target.value)} placeholder="Es. crema, terracotta" className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Taglia / dimensione</label>
                  <input data-testid="sm-size" value={form.size_preference} onChange={(e) => setField("size_preference", e.target.value)} placeholder="Es. M, oppure 30x40 cm" className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Budget orientativo</label>
                  <input data-testid="sm-budget" value={form.budget} onChange={(e) => setField("budget", e.target.value)} placeholder="Es. 80-120€" className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Per quando ti serve?</label>
                  <input data-testid="sm-date" type="date" value={form.desired_date} onChange={(e) => setField("desired_date", e.target.value)} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border focus:border-alimi-ink outline-none text-sm" />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Immagine di ispirazione (opzionale)</label>
                {form.inspiration_image ? (
                  <div className="relative inline-block">
                    <img src={form.inspiration_image} alt="Ispirazione" className="w-40 h-48 object-cover" />
                    <button type="button" onClick={() => setField("inspiration_image", "")} className="absolute -top-2 -right-2 bg-alimi-ink text-white rounded-full p-1" data-testid="sm-remove-img"><X size={12} /></button>
                  </div>
                ) : (
                  <label className="btn-outline cursor-pointer inline-flex">
                    <Upload size={14} /> Carica immagine
                    <input data-testid="sm-image" type="file" accept="image/*" onChange={onImage} className="hidden" />
                  </label>
                )}
                <p className="text-xs text-alimi-inkSoft mt-2">Max 2MB. Una foto, uno schizzo, un riferimento che ti piace.</p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-alimi-inkSoft">* Campi obbligatori. La richiesta non è impegnativa.</p>
                <button data-testid="sm-submit" type="submit" disabled={sending} className="btn-primary">
                  {sending ? "Invio…" : "Invia la richiesta"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
